(ns ui.editor2.schema
  "The DataScript schema for the editor2, which uses a DataScript db as its file format.")

(def schema
  {
   ;; ----------------------------------------------------------
   ;; Assets (video, audio, image, etc.)
   ;; ----------------------------------------------------------
   :asset/uuid
   {:db/unique :db.unique/identity
    :db/doc "Unique UUID for each asset (video/audio file, image, etc.)"}

   :asset/type
   { ;; :db/valueType :db.type/keyword
    :db/doc "Type of the asset, e.g. :video, :audio, :image, :fabricjs"}

   :asset/uri
   { ;; :db/valueType :db.type/string
    :db/doc "File path, URL, or other resource identifier"}

   :asset/data
   { ;; :db/valueType :db.type/string
    :db/doc "Used to directly embed the asset data instead of referencing it via the `:asset/uri`. Depending on the `:asset/type` it is nil, EDN (:fabricjs) or SVG for example."}

   :asset/content-type
   { ;; :db/valueType :db.type/string
    :db/doc "The content-type of the asset."}

   :asset/name
   { ;; :db/valueType :db.type/string
    :db/doc "The name of the asset (filename)."}

   :asset/width
   { ;; :db/valueType :db.type/double
    :db/doc "Width of the asset."}

   :asset/height
   { ;; :db/valueType :db.type/double
    :db/doc "Height of the asset."}

   :asset/duration
   { ;; :db/valueType :db.type/long
    ;; Milliseconds were chosen to avoid float arithmetic issues, when using
    ;; float-based seconds for example.
    :db/doc "Duration in milliseconds"
    }

   :asset/words
   {:db/valueType :db.type/ref
    :db/cardinality :db.cardinality/many
    :db/isComponent true
    :db/doc "The transcript of a media asset (:video, :audio): word entities with :word/uuid, :word/text, :word/start and :word/end (milliseconds, relative to the media), :word/index and optional per-word styling like :word/emphasis? and :word/break? (a phrase starts with the word). Components, so retracting the asset retracts its words. Every clip of the media with :clip/captions shows its own range of them (see slices/captions)."}

   :word/uuid
   {:db/unique :db.unique/identity
    :db/doc "Unique UUID for each transcript word of a media asset."}


   ;; ----------------------------------------------------------
   ;; Clips (represent a segment of an asset placed on a track)
   ;; ----------------------------------------------------------
   :clip/uuid
   {:db/unique :db.unique/identity
    :db/doc "Unique UUID for each clip on the timeline"}

   :clip/asset
   {:db/valueType :db.type/ref
    :db/cardinality :db.cardinality/one
    :db/doc "Reference to the asset entity used by this clip"}

   :clip/start
   { ;; :db/valueType :db.type/long
    :db/doc "Start time on the timeline (in milliseconds)"}

   :clip/end
   { ;; :db/valueType :db.type/long
    :db/doc "End time on the timeline (in milliseconds)"}

   :clip/name
   { ;; :db/valueType :db.type/string
    :db/doc "Name of the clip"}

   :clip/offset
   { ;; :db/valueType :db.type/long
    :db/doc "Offset in milliseconds into the asset (if the clip starts in the middle of the asset)."}

   :clip/track
   {:db/valueType :db.type/ref
    :db/cardinality :db.cardinality/one
    :db/doc         "Which track this clip belongs to"}

   :clip/muted
   { ;; :db/valueType :db.type/boolean
    :db/doc "When true, audio of this clip is muted during playback and rendering."}

   :clip/captions
   { ;; :db/valueType :db.type/map
    :db/doc "The captions of a :video clip as a map, e.g. {:captions/style \"highlight\"}: the words of the clip's asset (:asset/words) that fall into the clip's range are shown in that style, as an overlay above all other clips (see slices/captions). Absent when the clip has no captions."}

   :clip/transform
   {;; :db/valueType :edn
    :db/doc "The data that will be used for the CSS 'transform' attribute to position, scale and rotate the clip on the design. Examle: {:translate [104.4 210.8 0], :scale [1.115 1.116 1], :rotate 45}"}


   ;; ----------------------------------------------------------
   ;; Transitions (between two clips)
   ;; ----------------------------------------------------------
   ;; :transition/uuid
   ;; {:db/unique :db.unique/identity
   ;;  :db/doc    "Unique UUID for each transition"}

   ;; :transition/type
   ;; {:db/valueType :db.type/keyword
   ;;  :db/doc       "Type of transition, e.g. :fade, :wipe, :dissolve"}

   ;; :transition/duration
   ;; {:db/valueType :db.type/long
   ;;  :db/doc       "How long the transition lasts on the timeline"}

   ;; :transition/from-clip
   ;; {:db/valueType :db.type/ref
   ;;  :db/doc       "Clip entity where the transition begins"}

   ;; :transition/to-clip
   ;; {:db/valueType :db.type/ref
   ;;  :db/doc       "Clip entity where the transition ends"}


   ;; ----------------------------------------------------------
   ;; Tracks (a collection of clips, transitions, etc.). On the UI it is a lane in the timeline.
   ;; ----------------------------------------------------------
   :track/uuid
   {:db/unique :db.unique/identity
    :db/doc    "Unique UUID for each track (audio track, video track, etc.)"}

   ;; Use `:clip/_track` to get all clips that belong to this track.
   ;; `:track/clips` would allow that clips are part of multiple tracks, which
   ;; is not allowed.

   ;; :track/transitions
   ;; {:db/valueType :db.type/ref
   ;;  :db/cardinality :db.cardinality/many
   ;;  :db/doc         "All transitions that are placed on this track"}


   ;; ----------------------------------------------------------
   ;; Timeline (container for all tracks)
   ;; ----------------------------------------------------------
   :timeline/uuid
   {:db/unique :db.unique/identity
    :db/doc    "Unique UUID for the timeline"}

   :timeline/name
   { ;; :db/valueType :db.type/string
    :db/doc       "Project or timeline name"}

   :timeline/tracks
   {:db/doc "The order of the tracks as vector of UUIDs."}

   :timeline/slides
   {:db/doc "The order of the slides as vector of UUIDs."}

   ;; ----------------------------------------------------------
   ;; Slides (one Instagram Story slide)
   ;; ----------------------------------------------------------
   :slide/uuid
   {:db/unique :db.unique/identity
    :db/doc    "Unique UUID for each slide"}

   ;; :slide/asset
   ;; {:db/valueType :db.type/ref
   ;;  :db/cardinality :db.cardinality/one
   ;;  :db/doc "Reference to the main asset used by this slide, e.g. :image, :video, :fabricjs"}

   :slide/asset
   {:db/valueType :db.type/ref
    :db/doc "Ref to the asset with the FabricJS EDN with all Instagram Stories stickers."}

   :slide/duration
   { ;; :db/valueType :db.type/long
    :db/doc "Length of the slide (in milliseconds). There is never a gap between slides, therefore `:slide/duration` and the total order of the slides `:timeline/slides` are sufficient."}

   :slide/background-color
   {:db/doc "The background color of the slide."}

   })
